/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 24/05/22
 */

import { namedMemo } from '@/utils/namedMemo';
import { Box, Flex, HStack, IconButton, Text, Tooltip } from '@chakra-ui/react';
import { ArrayLayoutProps, composePaths, ControlElement, createDefaultValue, findUISchema } from '@jsonforms/core';
import { range } from 'lodash';
import merge from 'lodash/merge';
import React, { useMemo, useState } from 'react';
import { withJsonFormsRowCount } from '../../utils/withJsonFormsRowCount';
import ChakraListWithDetailItem from './ChakraListWithDetailItem';
import Icons from '@/components/icons/Icons';
interface ChakraListWithDetailProp extends Omit<ArrayLayoutProps, 'data'> {
    count: number;
    hideListControl?: boolean;
}
const ChakraListWithDetailItems = namedMemo(
    'ChakraListWithDetailItems',
    ({
        count,
        path,
        schema,
        uischema,
        removeItems,
        uischemas,
        config,
        addItem,
        label,
        enabled,
        hideListControl,
    }: ChakraListWithDetailProp) => {
        const foundUISchema = useMemo(
            () =>
                findUISchema(
                    // eslint-disable-next-line  @typescript-eslint/no-non-null-assertion
                    uischemas!,
                    schema,
                    uischema.scope,
                    path,
                    undefined,
                    uischema,
                ),
            [uischemas, schema, uischema.scope, path, uischema],
        ) as ControlElement;

        const appliedUiSchemaOptions = merge({}, config, uischema.options);
        const readonly = appliedUiSchemaOptions.readonly;

        const [focus, setFocus] = useState(-1);

        return (
            <Box border={'1px solid'} borderTop={'none'} borderColor={'gray.200'}>
                {range(count).map((index) => {
                    const childPath = composePaths(path, `${index}`);
                    return (
                        <ChakraListWithDetailItem
                            childPath={childPath}
                            schema={schema}
                            uischema={foundUISchema}
                            removeItem={removeItems && removeItems(path, [index])}
                            key={index}
                            visible={true}
                            isReadonly={readonly}
                            autoFocus={index === focus}
                            hideListControl={hideListControl}
                        />
                    );
                })}
                <HStack width={'100%'} justifyContent={'space-between'} textAlign={'center'} p={3} px={6}>
                    <Flex justifyContent={'start'}>{count <= 0 && <Text>No data</Text>}</Flex>
                    {enabled && !hideListControl && (
                        <Flex justifyContent={'flex-end'}>
                            <Tooltip id="tooltip-add" label={`Add to ${label}`}>
                                <IconButton
                                    aria-label={`Add to ${label}`}
                                    onClick={() => {
                                        setFocus(count);
                                        addItem(path, createDefaultValue(schema))();
                                    }}
                                    icon={<Icons.AddIcon />}
                                />
                            </Tooltip>
                        </Flex>
                    )}
                </HStack>
            </Box>
        );
    },
);

export default withJsonFormsRowCount(ChakraListWithDetailItems);
